import { useState } from "react";
import { Sparkles, ShieldCheck, PhoneCall, ArrowRight, Check, Snowflake, Layers, Package, Flame } from "lucide-react";
import IASimulatorWizard from "@/components/IASimulatorWizard";

const PROJECTS_HIGHLIGHT = [
  { icon: Snowflake, label: "Pompe à chaleur", tint: "#0055A4", bg: "#EFF6FF" },
  { icon: Flame, label: "Chaudière biomasse", tint: "#EA580C", bg: "#FFF7ED" },
  { icon: Layers, label: "Isolation thermique", tint: "#10B981", bg: "#ECFDF5" },
  { icon: Package, label: "Menuiseries", tint: "#0369A1", bg: "#EFF6FF" },
];

const AIDS = [
  { title: "MaPrimeRénov'", desc: "Aide de l'État versée par l'Anah selon vos revenus et le type de travaux." },
  { title: "Certificats d'Économies d'Énergie (CEE)", desc: "Primes versées par les énergéticiens obligés en fonction de la zone climatique et de la performance." },
];

export default function SimulateurIA() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <div className="min-h-screen bg-white pt-32 pb-24">
        {/* HERO */}
        <section className="relative overflow-hidden">
          <div
            className="absolute inset-0 opacity-[0.04] pointer-events-none"
            style={{ backgroundImage: "radial-gradient(#0055A4 1px, transparent 1px)", backgroundSize: "24px 24px" }}
          />
          <div className="max-w-6xl mx-auto px-6 sm:px-8 relative">
            <div className="inline-flex items-center gap-2 rounded-full bg-[#EFF6FF] text-[#0055A4] text-[11px] tracking-[.22em] uppercase font-semibold px-3 py-1.5 mb-6">
              <Sparkles className="h-3.5 w-3.5" /> ECONATION IA · Simulateur National · Barèmes 2026
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.05] mb-6" data-testid="ia-page-title">
              Estimez toutes vos aides<br />
              <span className="bg-gradient-to-r from-[#0055A4] via-[#0369A1] to-[#10B981] bg-clip-text text-transparent">en 2 minutes chrono</span>
            </h1>

            <p className="text-lg text-slate-600 leading-relaxed max-w-2xl mb-8">
              Notre intelligence artificielle croise les <strong>barèmes officiels 2026</strong> pour vous restituer instantanément
              le montant de votre <strong>MaPrimeRénov'</strong> et de vos <strong>primes CEE</strong>.
              Un spécialiste ECONATION vous rappelle pour tout confirmer.
            </p>

            <div className="flex flex-wrap gap-3 items-center mb-10">
              <button
                onClick={() => setOpen(true)}
                data-testid="ia-hero-cta"
                className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#0055A4] to-[#003D7A] hover:from-[#003D7A] hover:to-[#0055A4] px-7 py-3.5 text-white text-sm font-semibold tracking-wide shadow-xl shadow-[#0055A4]/30 transition"
              >
                <Sparkles className="h-4 w-4" /> Lancer mon estimation
                <ArrowRight className="h-4 w-4" />
              </button>
              <div className="inline-flex items-center gap-2 text-slate-500 text-xs">
                <ShieldCheck className="h-4 w-4 text-[#10B981]" />
                Gratuit · sans engagement · RGPD
              </div>
            </div>

            {/* Project highlights */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {PROJECTS_HIGHLIGHT.map((p, i) => {
                const Ic = p.icon;
                return (
                  <div key={i} className="rounded-2xl border border-slate-200 bg-white p-4 flex items-center gap-3 hover:shadow-md transition">
                    <div className="h-10 w-10 rounded-lg grid place-items-center flex-shrink-0" style={{ background: p.bg, color: p.tint }}>
                      <Ic className="h-5 w-5" />
                    </div>
                    <div className="text-slate-900 font-semibold text-sm">{p.label}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* AIDES DETAILLEES */}
        <section className="mt-24 max-w-6xl mx-auto px-6 sm:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 text-slate-700 text-[11px] tracking-[.2em] uppercase font-semibold px-3 py-1.5 mb-4">
              2 dispositifs cumulables
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-3">
              MaPrimeRénov' + CEE, calculés en une seule simulation
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Nous croisons les deux dispositifs officiels d'État et énergéticiens pour maximiser vos aides.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 max-w-3xl mx-auto">
            {AIDS.map((a, i) => (
              <div key={i} className="rounded-2xl border border-slate-200 bg-white p-6 hover:shadow-lg transition">
                <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#0055A4] to-[#003D7A] text-white grid place-items-center mb-4">
                  <Check className="h-5 w-5" strokeWidth={3} />
                </div>
                <div className="text-slate-900 font-semibold text-base mb-1.5">{a.title}</div>
                <div className="text-slate-600 text-sm leading-relaxed">{a.desc}</div>
              </div>
            ))}
          </div>
        </section>

        {/* CALLBACK BAND */}
        <section className="mt-24 max-w-6xl mx-auto px-6 sm:px-8">
          <div className="rounded-3xl bg-gradient-to-br from-[#0055A4] via-[#003D7A] to-[#0A2540] p-8 sm:p-12 text-white relative overflow-hidden">
            <div
              className="absolute inset-0 opacity-10 pointer-events-none"
              style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.4) 1px, transparent 1px)", backgroundSize: "24px 24px" }}
            />
            <div className="relative grid md:grid-cols-[1fr_auto] gap-8 items-center">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full bg-white/15 text-white text-[10px] tracking-[.22em] uppercase font-semibold px-3 py-1.5 mb-4">
                  <PhoneCall className="h-3.5 w-3.5" /> Étape finale
                </div>
                <h3 className="text-3xl sm:text-4xl font-bold mb-3 tracking-tight">
                  Un spécialiste vous rappelle sous 24h
                </h3>
                <p className="text-white/85 leading-relaxed max-w-xl">
                  Après votre simulation, un conseiller ECONATION vous rappelle pour <strong>confirmer le montant</strong> de
                  toutes vos aides et vous accompagner dans le dépôt du dossier auprès de l'Anah et des obligés CEE.
                </p>
              </div>
              <button
                onClick={() => setOpen(true)}
                data-testid="ia-cta-band"
                className="inline-flex items-center gap-2 rounded-full bg-white hover:bg-slate-100 px-7 py-4 text-[#0055A4] text-sm font-semibold tracking-wide shadow-xl transition"
              >
                <Sparkles className="h-4 w-4" /> Simuler maintenant
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </section>
      </div>

      <IASimulatorWizard open={open} onClose={() => setOpen(false)} />
    </>
  );
}
